import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import {
    AdminHighlightAutomationConfigurationUpdateDto,
    HighlightCreateDto,
    HighlightDto,
    HighlightRolloverIntervalUnit
} from '../../../types/admin.types';
import {
    createAdminHighlight,
    deleteAdminHighlight,
    fetchAdminHighlights,
    fetchHighlightAutomationConfiguration,
    updateAdminHighlight,
    updateHighlightAutomationConfiguration
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import ImagePickerField from './components/ImagePickerField';
import AdminDateTimeInput from './components/AdminDateTimeInput';
import { toDateTimeLocalInput, toUtcIsoString } from '../../../utils/date-time';
import { isSuperAdmin } from '../../../utils/roles';

const emptyForm: HighlightCreateDto = {
    title: '',
    description: '',
    imageUrl: '',
    videoUri: '',
    startDate: '',
    endDate: '',
    isActive: true,
    sortOrder: 0
};

const AdminHighlights = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<HighlightDto[]>([]);
    const [form, setForm] = useState<HighlightCreateDto>(emptyForm);
    const [configForm, setConfigForm] = useState<AdminHighlightAutomationConfigurationUpdateDto | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [configErrorMessage, setConfigErrorMessage] = useState<string | null>(null);
    const [configSuccessMessage, setConfigSuccessMessage] = useState<string | null>(null);
    const user = getUserInfo();
    const token = user?.token;
    const canManageAutomation = isSuperAdmin(user?.roles);

    const loadHighlights = async () => {
        try {
            const response = await fetchAdminHighlights(token);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load highlights.');
        }
    };

    const loadConfig = async () => {
        if (!canManageAutomation) {
            setConfigForm(null);
            return;
        }

        try {
            const response = await fetchHighlightAutomationConfiguration(token);
            setConfigForm({
                Id: response.Id,
                EnableDynamicHighlights: response.EnableDynamicHighlights ?? false,
                HighlightsRolloverIntervalValue: response.HighlightsRolloverIntervalValue ?? 6,
                HighlightsRolloverIntervalUnit: response.HighlightsRolloverIntervalUnit ?? 'Hours',
                HighlightsGeneratedCount: response.HighlightsGeneratedCount ?? 3,
                HighlightsTemplateSet: response.HighlightsTemplateSet ?? 'default'
            });
        } catch (error) {
            setConfigErrorMessage('Unable to load highlights automation settings.');
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        setConfigErrorMessage(null);
        await Promise.all([loadHighlights(), loadConfig()]);
        setIsLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
    };

    const handleEdit = (item: HighlightDto) => {
        setSelectedId(item.id);
        setForm({
            title: item.title,
            description: item.description ?? '',
            imageUrl: item.imageUrl,
            videoUri: item.videoUri,
            startDate: toDateTimeLocalInput(item.startDate),
            endDate: toDateTimeLocalInput(item.endDate),
            isActive: item.isActive,
            sortOrder: item.sortOrder
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        const payload: HighlightCreateDto = {
            ...form,
            startDate: toUtcIsoString(form.startDate),
            endDate: toUtcIsoString(form.endDate)
        };

        try {
            if (selectedId) {
                await updateAdminHighlight(selectedId, payload, token);
            } else {
                await createAdminHighlight(payload, token);
            }
            await loadHighlights();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save highlight.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteAdminHighlight(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadHighlights();
        } catch (error) {
            setErrorMessage('Unable to delete highlight.');
        }
    };

    const handleConfigSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!configForm) {
            return;
        }

        const normalizedIntervalValue = Math.max(1, Math.min(99, Number(configForm.HighlightsRolloverIntervalValue ?? 6)));
        const normalizedGeneratedCount = Math.max(1, Math.min(99, Number(configForm.HighlightsGeneratedCount ?? 3)));

        const payload: AdminHighlightAutomationConfigurationUpdateDto = {
            ...configForm,
            EnableDynamicHighlights: Boolean(configForm.EnableDynamicHighlights),
            HighlightsRolloverIntervalValue: normalizedIntervalValue,
            HighlightsRolloverIntervalUnit: configForm.HighlightsRolloverIntervalUnit ?? 'Hours',
            HighlightsGeneratedCount: normalizedGeneratedCount,
            HighlightsTemplateSet: configForm.HighlightsTemplateSet ?? 'default'
        };

        setConfigErrorMessage(null);
        setConfigSuccessMessage(null);
        setIsSavingConfig(true);

        try {
            await updateHighlightAutomationConfiguration(payload, token);
            setConfigSuccessMessage('Highlights automation settings saved.');
            await loadConfig();
        } catch (error) {
            setConfigErrorMessage('Unable to save highlights automation settings.');
        } finally {
            setIsSavingConfig(false);
        }
    };

    const handleIntervalUnitChange = (value: string) => {
        if (!configForm) {
            return;
        }

        const units: HighlightRolloverIntervalUnit[] = ['Minutes', 'Hours', 'Days', 'Weeks'];
        const nextUnit = units.includes(value as HighlightRolloverIntervalUnit)
            ? (value as HighlightRolloverIntervalUnit)
            : 'Hours';

        setConfigForm({ ...configForm, HighlightsRolloverIntervalUnit: nextUnit });
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Highlights</h1>
                    <p>Manage homepage highlights.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                {canManageAutomation ? (
                    <section className={styles.panel}>
                        <h2>Highlights Automation</h2>
                        {configErrorMessage ? <div className={styles.muted}>{configErrorMessage}</div> : null}
                        {configSuccessMessage ? <div className={styles.muted}>{configSuccessMessage}</div> : null}
                        {configForm ? (
                            <form onSubmit={handleConfigSubmit}>
                                <div className={styles.field}>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(configForm.EnableDynamicHighlights)}
                                            onChange={(event) => setConfigForm({ ...configForm, EnableDynamicHighlights: event.target.checked })}
                                        />
                                        {' '}Enable auto-generated highlights
                                    </label>
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="highlight-rollover-interval">Rollover Interval</label>
                                    <input
                                        id="highlight-rollover-interval"
                                        type="number"
                                        min={1}
                                        max={99}
                                        value={configForm.HighlightsRolloverIntervalValue ?? 6}
                                        onChange={(event) =>
                                            setConfigForm({
                                                ...configForm,
                                                HighlightsRolloverIntervalValue: event.target.value === '' ? 1 : Number(event.target.value)
                                            })
                                        }
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="highlight-rollover-unit">Interval Unit</label>
                                    <select
                                        id="highlight-rollover-unit"
                                        value={configForm.HighlightsRolloverIntervalUnit ?? 'Hours'}
                                        onChange={(event) => handleIntervalUnitChange(event.target.value)}
                                    >
                                        <option value="Minutes">Minutes</option>
                                        <option value="Hours">Hours</option>
                                        <option value="Days">Days</option>
                                        <option value="Weeks">Weeks</option>
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <label htmlFor="highlight-generated-count">Generated Count</label>
                                    <select
                                        id="highlight-generated-count"
                                        value={configForm.HighlightsGeneratedCount ?? 3}
                                        onChange={(event) =>
                                            setConfigForm({ ...configForm, HighlightsGeneratedCount: Number(event.target.value) })
                                        }
                                    >
                                        {Array.from({ length: 99 }, (_, index) => index + 1).map((value) => (
                                            <option key={value} value={value}>
                                                {value}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.actions}>
                                    <button className={styles.primary} type="submit" disabled={isSavingConfig}>
                                        {isSavingConfig ? 'Saving...' : 'Save Automation Settings'}
                                    </button>
                                </div>
                            </form>
                        ) : configErrorMessage ? null : (
                            <div className={styles.muted}>Loading automation settings...</div>
                        )}
                    </section>
                ) : null}

                <section className={styles.panel}>
                    <h2>Current Highlights</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading highlights...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No highlights yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.title}</strong>
                                    <div className={styles.muted}>Sort: {item.sortOrder} · Active: {item.isActive ? 'Yes' : 'No'}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Highlight' : 'New Highlight'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="highlight-title">Title</label>
                            <input
                                id="highlight-title"
                                value={form.title}
                                onChange={(event) => setForm({ ...form, title: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="highlight-description">Description</label>
                            <textarea
                                id="highlight-description"
                                value={form.description ?? ''}
                                onChange={(event) => setForm({ ...form, description: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <ImagePickerField
                            label="Image URL"
                            value={form.imageUrl}
                            onChange={(next) => setForm({ ...form, imageUrl: next })}
                            token={token}
                            required
                            helperText="Pick from the library or paste an external URL."
                            preferredVariantType={1}
                        />
                        <div className={styles.field}>
                            <label htmlFor="highlight-video">Video Shortcode</label>
                            <input
                                id="highlight-video"
                                value={form.videoUri}
                                onChange={(event) => setForm({ ...form, videoUri: event.target.value })}
                                maxLength={200}
                                placeholder="DIe4rdPHP4pvC"
                                required
                            />
                            <div className={styles.muted}>
                                Use only the shortcode (the value after ?v=), not a full URL.
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="highlight-start">Start Date</label>
                            <AdminDateTimeInput
                                id="highlight-start"
                                value={form.startDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, startDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="highlight-end">End Date</label>
                            <AdminDateTimeInput
                                id="highlight-end"
                                value={form.endDate ?? ''}
                                includeTime
                                onChange={(value) => setForm({ ...form, endDate: value })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="highlight-sort">Sort Order</label>
                            <input
                                id="highlight-sort"
                                type="number"
                                value={form.sortOrder}
                                onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
                            />
                        </div>
                        <div className={styles.field}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                                />
                                {' '}Active
                            </label>
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Highlight' : 'Create Highlight'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminHighlights;
